import { useCallback, useEffect, useState } from "react";
import api, { formatApiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { TransactionHistory } from "@/components/TransactionHistory";
import AddBalanceButton from "@/components/AddBalanceButton";
import { toast } from "sonner";
import { Wallet, Gauge, Robot, SpeakerHigh, Microphone } from "@phosphor-icons/react";

const PRICE_ROWS = [
  { key: "call_limit_price_minor", label: "Call limit", unit: "/ channel", icon: Gauge },
  { key: "ai_model_price_minor", label: "AI Model", unit: "/ min", icon: Robot },
  {
    key: "tts_price_minor",
    label: "TTS",
    unit: "/ min",
    icon: SpeakerHigh,
    freeKey: "tts_billing_type",
  },
  {
    key: "stt_price_minor",
    label: "STT",
    unit: "/ min",
    icon: Microphone,
    freeKey: "stt_billing_type",
  },
];

export default function AccountBilling() {
  const { user } = useAuth();
  const isReseller = user?.role === "reseller";
  const [balance, setBalance] = useState(0);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [txnKey, setTxnKey] = useState(0);

  const loadBalance = useCallback(async () => {
    try {
      const reqs = [api.get("/billing/balance")];
      if (!isReseller) reqs.push(api.get("/billing/my-pricing"));
      const [bal, prices] = await Promise.all(reqs);
      setBalance(bal.data.cash_balance_minor || 0);
      if (prices) setPricing(prices.data || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  }, [isReseller]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadBalance();
      setLoading(false);
    })();
  }, [loadBalance]);

  return (
    <div className="space-y-5" data-testid="account-billing-page">
      <PageHeader
        title="Billing"
        subtitle={
          isReseller
            ? "Cash balance and transaction history."
            : "Balance, assigned rates, and transaction history."
        }
      />

      {/* 1. Balance + pricing */}
      <section
        className="rounded-xl border border-border bg-card card-shadow p-4 sm:p-5"
        data-testid="billing-overview"
      >
        <div className={`flex flex-col gap-4 ${isReseller ? "" : "lg:flex-row lg:items-stretch"}`}>
          <div
            className={`flex flex-col justify-between gap-3 ${
              isReseller
                ? "sm:flex-row sm:items-end sm:justify-between"
                : "lg:w-56 lg:shrink-0 lg:border-r lg:border-border lg:pr-5 border-b border-border pb-4 lg:pb-0 lg:border-b-0"
            }`}
          >
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Cash balance
              </p>
              <p
                className="mt-1 text-3xl font-semibold tabular-nums tracking-tight"
                data-testid="billing-balance"
              >
                {loading ? "…" : money(balance)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Available for concurrent-channel purchases
              </p>
            </div>
            <AddBalanceButton
              onSubmitted={() => {
                loadBalance();
                setTxnKey((k) => k + 1);
              }}
            />
          </div>

          {!isReseller && (
            <div className="min-w-0 flex-1">
              <div className="mb-2.5 flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Assigned pricing
                </p>
                {pricing?.call_limit_pricing_mode ? (
                  <p className="text-xs text-muted-foreground capitalize">
                    Mode: {pricing.call_limit_pricing_mode}
                  </p>
                ) : null}
              </div>
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading prices…</p>
              ) : (
                <div
                  className="grid grid-cols-2 gap-2 xl:grid-cols-4"
                  data-testid="assigned-pricing"
                >
                  {PRICE_ROWS.map((row) => {
                    const Icon = row.icon;
                    const isFree = row.freeKey && pricing?.[row.freeKey] === "free";
                    const value = pricing?.[row.key];
                    return (
                      <div
                        key={row.key}
                        className="rounded-lg border border-border/80 bg-muted/30 px-3 py-2.5"
                        data-testid={`assigned-pricing-${row.key}`}
                      >
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Icon className="h-3.5 w-3.5 text-primary" weight="duotone" />
                          <span className="text-[11px] font-medium truncate">{row.label}</span>
                        </div>
                        <p
                          className={`mt-1 text-lg font-semibold tabular-nums leading-none ${
                            isFree ? "text-emerald-700 dark:text-emerald-400" : ""
                          }`}
                        >
                          {isFree ? (
                            <>
                              Free
                              <span className="ml-1 text-[11px] font-normal text-muted-foreground">
                                / min
                              </span>
                            </>
                          ) : (
                            <>
                              {value == null ? "—" : money(value)}
                              {value != null && (
                                <span className="ml-1 text-[11px] font-normal text-muted-foreground">
                                  {row.unit}
                                </span>
                              )}
                            </>
                          )}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* 2–4. Total spent + filters + transaction table */}
      <TransactionHistory key={txnKey} />
    </div>
  );
}
